import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import {
  abandonDeflection, createDeflection, createTicket, getActiveDeflection,
  getKBArticles, uploadTicketAttachment, attachToTicket,
} from "@/lib/firestore";
import { previewTriage } from "@/lib/gemini";
import { searchKBArticles } from "@/lib/kbSearch";
import { detectPlatform } from "@/lib/platform";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { HelpDeskUser, KBArticle, PLATFORM_LABELS } from "@/types";
import { Spinner, toast } from "@/components/shared";

const MAX_SIZE = 25 * 1024 * 1024;

interface LocationState {
  prefill?: { title: string; description: string; origin?: "manual" | "ai-chat"; originChat?: string };
}

export default function NewTicket() {
  useDocumentTitle("New Ticket");
  const { helpDeskUser, firebaseUser } = useAuth();
  const isAgent = helpDeskUser?.role === "agent" || helpDeskUser?.role === "admin";
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = (location.state as LocationState | null)?.prefill;

  const [title, setTitle] = useState(prefill?.title ?? "");
  const [description, setDescription] = useState(prefill?.description ?? "");
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [triage, setTriage] = useState<{ category: string; priority: string; confidence: number; reason: string; suggestKB?: string } | null>(null);
  const [triaging, setTriaging] = useState(false);
  const [allArticles, setAllArticles] = useState<KBArticle[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const triageTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const detectedPlatform = useMemo(() => detectPlatform(), []);
  const [allUsers, setAllUsers] = useState<HelpDeskUser[]>([]);
  const [submitOnBehalfOf, setSubmitOnBehalfOf] = useState<string | null>(null);

  useEffect(() => {
    getKBArticles().then(setAllArticles).catch(() => { /* empty KB is fine */ });
  }, []);

  // Agents and admins can pick a different submitter — load active users.
  useEffect(() => {
    if (!isAgent) return;
    const unsub = onSnapshot(
      query(collection(db, "users"), where("status", "==", "active")),
      (snap) => setAllUsers(snap.docs.map((d) => ({ uid: d.id, ...d.data() } as HelpDeskUser))),
    );
    return unsub;
  }, [isAgent]);

  const selectedSubmitter = submitOnBehalfOf
    ? allUsers.find((u) => u.uid === submitOnBehalfOf)
    : null;

  const kbSuggestions = useMemo(() => {
    const query = `${title} ${description}`.trim();
    if (query.length < 6 || allArticles.length === 0) return [];
    return searchKBArticles(allArticles, query, detectedPlatform, triage?.suggestKB).slice(0, 3);
  }, [title, description, allArticles, detectedPlatform, triage?.suggestKB]);

  useEffect(() => {
    if (prefill?.title && prefill.title.length >= 8) onTitleChange(prefill.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTitleChange = (val: string) => {
    setTitle(val);
    if (triageTimer.current) clearTimeout(triageTimer.current);
    if (val.length < 8) { setTriage(null); return; }
    triageTimer.current = setTimeout(async () => {
      setTriaging(true);
      try { setTriage(await previewTriage(val)); }
      catch { /* silent fail */ }
      setTriaging(false);
    }, 700);
  };

  const addFiles = (newFiles: File[]) => {
    const valid = newFiles.filter((f) => {
      if (f.size > MAX_SIZE) { toast(`${f.name} exceeds 25MB`, "error"); return false; }
      return true;
    });
    setFiles((prev) => [...prev, ...valid]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) return;
    setSubmitting(true);
    try {
      const submitterId = selectedSubmitter?.uid ?? firebaseUser!.uid;
      const submitterEmail = selectedSubmitter?.email ?? firebaseUser!.email ?? "";
      const submitterName = selectedSubmitter?.displayName
        ?? helpDeskUser?.displayName
        ?? firebaseUser?.displayName
        ?? "";
      const id = await createTicket({
        title: title.trim(),
        description: description.trim(),
        submitterId,
        submitterEmail,
        submitterName,
        origin: prefill?.origin,
        originChat: prefill?.originChat,
      });

      // Upload attachments after the ticket exists so we have an ID
      for (const file of files) {
        const att = await uploadTicketAttachment(id, file, firebaseUser!.uid);
        await attachToTicket(id, att);
      }

      // Submitting a ticket means any prior deflection candidate didn't pan out.
      try {
        const active = await getActiveDeflection(firebaseUser!.uid);
        if (active) await abandonDeflection(active.id);
      } catch { /* not critical */ }

      toast("Ticket submitted — AI is triaging it now");
      navigate(`/tickets/${id}`);
    } catch {
      toast("Failed to submit ticket. Please try again.", "error");
    }
    setSubmitting(false);
  };

  const startDeflection = async (channel: "kb" | "ai-chat", target?: string): Promise<string | undefined> => {
    if (!firebaseUser) return;
    const issueTitle = title.trim();
    const issueDescription = description.trim();
    if (!issueTitle && !issueDescription) return;
    try {
      return await createDeflection({
        userId: firebaseUser.uid,
        userEmail: firebaseUser.email ?? "",
        userDisplayName: helpDeskUser?.displayName ?? firebaseUser.displayName ?? firebaseUser.email ?? "User",
        issueTitle,
        issueDescription,
        channel,
        channelTarget: target,
      });
    } catch { return undefined; }
  };

  const priColor: Record<string, string> = { P1: "var(--red-t)", P2: "var(--orange-t)", P3: "var(--blue-t)", P4: "var(--text2)" };
  const priBg: Record<string, string> = { P1: "var(--red-l)", P2: "var(--orange-l)", P3: "var(--blue-l)", P4: "var(--gray)" };

  return (
    <div style={{ overflowY: "auto", flex: 1, padding: 20 }}>
      <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>← Back</button>
      <h1 style={{ marginBottom: 4 }}>Submit a Ticket</h1>
      <p style={{ color: "var(--text2)", fontSize: 13, marginBottom: 20 }}>
        AI will triage, categorize, and assign this automatically.
      </p>

      {prefill?.origin === "ai-chat" && (
        <div style={{ maxWidth: 560, marginBottom: 12, padding: "10px 12px", background: "var(--gem-l)", color: "var(--gem-t)", borderRadius: 6, fontSize: 12 }}>
          <strong>From AI chat:</strong> the title and description below were drafted from your conversation. Edit anything before submitting.
        </div>
      )}

      <div style={{ maxWidth: 560 }}>
        <form onSubmit={handleSubmit}>
          <div className="card card-body" style={{ marginBottom: 14 }}>
            <div className="fg">
              <label>Issue Title *</label>
              <input className="fi" value={title} onChange={(e) => onTitleChange(e.target.value)}
                placeholder="Brief description of the problem" required />
            </div>

            {(triaging || triage) && (
              <div style={{
                background: "var(--gem-l)", border: "1px solid rgba(26,115,232,.2)",
                borderRadius: "var(--rs)", padding: "10px 12px", marginTop: 8,
                fontSize: 12, color: "var(--gem-t)",
              }}>
                {triaging ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Spinner size={14} color="var(--gem)" />
                    Analyzing…
                  </div>
                ) : triage && (
                  <>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>
                      ✓ AI Triage Preview — {triage.confidence}% confidence
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <span className="badge badge-gem">{triage.category}</span>
                      <span className="badge" style={{ background: priBg[triage.priority], color: priColor[triage.priority] }}>{triage.priority}</span>
                    </div>
                    <div style={{ marginTop: 6, opacity: 0.85 }}>{triage.reason}</div>
                  </>
                )}
              </div>
            )}

            {kbSuggestions.length > 0 && (
              <div style={{
                background: "var(--green-l)", border: "1px solid rgba(40,205,65,.25)",
                borderRadius: "var(--rs)", padding: "10px 12px", marginTop: 10,
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--green-t)", marginBottom: 6 }}>
                  💡 These articles might already answer your question
                </div>
                <div style={{ fontSize: 11, color: "var(--green-t)", marginBottom: 8, opacity: 0.85 }}>
                  Click one to see if it resolves your issue — you can always come back and submit a ticket.
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  {kbSuggestions.map((a) => (
                    <button key={a.id} type="button" onClick={async () => {
                      const deflectionId = await startDeflection("kb", a.id);
                      navigate(`/kb/${a.id}`, deflectionId ? { state: { deflectionId } } : undefined);
                    }}
                      style={{
                        textAlign: "left", background: "var(--surface)", border: "0.5px solid var(--border)",
                        borderRadius: 6, padding: "8px 10px", cursor: "pointer",
                        display: "flex", alignItems: "center", gap: 8, fontFamily: "var(--font)",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--green)")}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--border)")}
                    >
                      <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>{a.title}</span>
                      {(a.platforms ?? []).map((p) => (
                        <span key={p} className="badge" style={{ fontSize: 10, background: "var(--gray)", color: "var(--text2)" }}>{PLATFORM_LABELS[p]}</span>
                      ))}
                      <span className="badge badge-gem" style={{ fontSize: 10 }}>{a.category}</span>
                      <span style={{ color: "var(--text3)", fontSize: 14 }}>→</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isAgent && (
              <div className="fg" style={{ marginTop: 14 }}>
                <label>Submit on behalf of <span style={{ color: "var(--text2)", fontWeight: 400 }}>— optional, defaults to you</span></label>
                <select className="fsel" value={submitOnBehalfOf ?? ""}
                  onChange={(e) => setSubmitOnBehalfOf(e.target.value || null)}>
                  <option value="">Myself ({helpDeskUser?.displayName ?? firebaseUser?.email})</option>
                  {allUsers
                    .filter((u) => u.uid !== firebaseUser?.uid)
                    .sort((a, b) => (a.displayName || a.email).localeCompare(b.displayName || b.email))
                    .map((u) => (
                      <option key={u.uid} value={u.uid}>
                        {u.displayName || u.email} ({u.email}) · {u.role}
                      </option>
                    ))}
                </select>
                {selectedSubmitter && (
                  <div style={{ fontSize: 12, color: "var(--blue-t)", marginTop: 6, padding: "6px 10px", background: "var(--blue-l)", borderRadius: 6 }}>
                    Submitting as <strong>{selectedSubmitter.displayName || selectedSubmitter.email}</strong>. This person will receive ticket emails and own the ticket.
                  </div>
                )}
              </div>
            )}

            <div className="fg" style={{ marginTop: 14 }}>
              <label>Description *</label>
              <textarea className="fta" value={description} onChange={(e) => setDescription(e.target.value)}
                placeholder="Include steps to reproduce, error messages, OS version, device type…" required />
            </div>

            <div className="fg" style={{ marginTop: 14 }}>
              <label>Attachments (optional)</label>
              <div
                onClick={() => fileInput.current?.click()}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
                onDrop={(e) => { e.preventDefault(); addFiles(Array.from(e.dataTransfer.files)); }}
                style={{
                  border: "1.5px dashed var(--border-med)", borderRadius: "var(--rs)",
                  padding: 14, textAlign: "center", cursor: "pointer", background: "var(--gray)",
                  fontSize: 12, color: "var(--text2)",
                }}
              >
                <input ref={fileInput} type="file" multiple style={{ display: "none" }}
                  onChange={(e) => addFiles(Array.from(e.target.files ?? []))} />
                📎 Drop files or click to browse — up to 25MB each
              </div>
              {files.length > 0 && (
                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                  {files.map((f, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "5px 8px", background: "var(--gray)", borderRadius: 4 }}>
                      <span style={{ flex: 1 }}>{f.name}</span>
                      <span style={{ color: "var(--text2)" }}>{(f.size / 1024).toFixed(1)} KB</span>
                      <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--red-t)", fontSize: 14 }}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? <><Spinner size={14} color="white" /> Submitting…</> : "Submit Ticket"}
            </button>
            <button type="button" className="btn btn-secondary"
              onClick={async () => {
                const trimmedTitle = title.trim();
                const trimmedDesc = description.trim();
                const seed = trimmedTitle && trimmedDesc
                  ? `${trimmedTitle}\n\n${trimmedDesc}`
                  : trimmedTitle || trimmedDesc;
                const deflectionId = await startDeflection("ai-chat");
                const state: Record<string, unknown> = {};
                if (seed) state.seedMessage = seed;
                if (deflectionId) state.deflectionId = deflectionId;
                navigate("/chat", Object.keys(state).length ? { state } : undefined);
              }}
            >
              Try AI Assistant First →
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
